/**
 * TelemetryProvider — wires a real onTrack implementation into the tree.
 *
 * Design
 * ------
 * - Consumers call `useTelemetry().track(eventName, payload?)`.
 * - The provider is the only place that knows which analytics backend
 *   (GA4, Segment, custom) receives events — primitives stay agnostic.
 * - Memoised context value prevents unnecessary re-renders when the
 *   provider re-renders for reasons unrelated to telemetry.
 * - exactOptionalPropertyTypes compliant.
 */

import { createContext, useMemo } from 'react';
import type { ReactNode } from 'react';
import type { TelemetryHook, TrackFn } from './useTelemetry';

// ── Context ────────────────────────────────────────────────────────────────

/**
 * Exported so useTelemetry.ts can import it without a circular dependency.
 * Consumers should use `useTelemetry()` — not this context directly.
 */
export const TelemetryContext = createContext<TelemetryHook | null>(null);

// ── Provider ───────────────────────────────────────────────────────────────

type TelemetryProviderProps = {
    /** Called for every track() invocation from a descendant. */
    onTrack: TrackFn;
    children: ReactNode;
};

/**
 * Mount once near the tree root (e.g. next to ToastProvider, UserProvider).
 * Pass the real analytics sink as `onTrack`; all descendants receive it via
 * `useTelemetry()`.
 *
 * @example
 * ```tsx
 * <TelemetryProvider onTrack={(name, payload) => analytics.track(name, payload)}>
 *   <App />
 * </TelemetryProvider>
 * ```
 */
export function TelemetryProvider({ onTrack, children }: TelemetryProviderProps) {
    const value = useMemo<TelemetryHook>(
        () => ({ track: onTrack }),
        [onTrack],
    );

    return (
        <TelemetryContext.Provider value={value}>
            {children}
        </TelemetryContext.Provider>
    );
}
